/** @format */
import { CustomerDetailsFormValues } from '@toucan/types/customer';

import { getConfig } from '@toucan/helpers/settings';

import {
    AmendedTransaction,
    CreatedTransactionInfo,
} from '@toucan/components/organisms/checkout/types';
import { ShippingFormValues } from '@toucan/components/organisms/transaction-address-form/helpers/use-shipping-options';
import { TransactionAddressFormValues } from '@toucan/components/organisms/transaction-address-form/types';

import {
    CheckoutSubmissionError,
    CheckoutType,
    PaymentGateway,
} from '@toucan/client/core/checkout/config/types';
import { ShippingOption } from '@toucan/client/core/shipping';
import { TransactionMetadata } from '@toucan/client/features/transaction';
import {
    AddressWithId,
    MixedTransaction,
    TransactionSummary,
} from '@toucan/client/global/transaction';

export const initialState: CheckoutState = {
    addressBook: [],
    amendedTransaction: undefined,
    parentTransaction: undefined,
    shippingOptions: [],
    createdTransactionInfo: undefined,
    shippingDetails: undefined,
    customerDetails: undefined,
    billingAddress: undefined,
    isSubmitting: false,
    isComplete: false,
    submissionError: null,
    finalCheckoutType: undefined,
    paymentGateways: undefined,
    checkoutId: '',
    isPaymentLink: false,
    isLoading: true,
    transaction: undefined,
    transactionMetadata: {} as TransactionMetadata,
    isPromoCodeSubmitting: false,
    useBalance: false,
    balanceAmount: 0,
};

export type CheckoutState = {
    isPaymentLink: boolean;
    isLoading: boolean;
    addressBook: AddressWithId[];
    amendedTransaction?: AmendedTransaction;
    parentTransaction?: TransactionSummary;
    shippingOptions: ShippingOption[];
    createdTransactionInfo?: CreatedTransactionInfo;
    shippingDetails?: ShippingFormValues;
    customerDetails?: CustomerDetailsFormValues;
    billingAddress?: TransactionAddressFormValues;
    isSubmitting: boolean;
    isComplete: boolean;
    submissionError: CheckoutSubmissionError | null;
    finalCheckoutType?: CheckoutType;
    paymentGateways?: PaymentGateway[];
    checkoutId: string;
    transaction?: MixedTransaction;
    transactionMetadata: TransactionMetadata;
    isPromoCodeSubmitting: boolean;
    useBalance: boolean;
    balanceAmount: number;
};

export type Updater<T> = T | ((prev: T | undefined) => T | undefined);

export enum CheckoutActionType {
    SET_ADDRESS_BOOK = 'SET_ADDRESS_BOOK',
    SET_AMENDED_TRANSACTION = 'SET_AMENDED_TRANSACTION',
    SET_PARENT_TRANSACTION = 'SET_PARENT_TRANSACTION',
    SET_SHIPPING_OPTIONS = 'SET_SHIPPING_OPTIONS',
    SET_CREATED_TRANSACTION_INFO = 'SET_CREATED_TRANSACTION_INFO',
    SET_SHIPPING_DETAILS = 'SET_SHIPPING_DETAILS',
    SET_CUSTOMER_DETAILS = 'SET_CUSTOMER_DETAILS',
    SET_BILLING_ADDRESS = 'SET_BILLING_ADDRESS',
    SET_IS_SUBMITTING = 'SET_IS_SUBMITTING',
    SET_IS_COMPLETE = 'SET_IS_COMPLETE',
    SET_SUBMISSION_ERROR = 'SET_SUBMISSION_ERROR',
    SET_FINAL_CHECKOUT_TYPE = 'SET_FINAL_CHECKOUT_TYPE',
    SET_PAYMENT_GATEWAYS = 'SET_PAYMENT_GATEWAYS',
    SET_CHECKOUT_ID = 'SET_CHECKOUT_ID',
    SET_IS_PAYMENT_LINK = 'SET_IS_PAYMENT_LINK',
    SET_IS_LOADING = 'SET_IS_LOADING',
    SET_TRANSACTION = 'SET_TRANSACTION',
    SET_TRANSACTION_METADATA = 'SET_TRANSACTION_METADATA',
    SET_IS_PROMO_CODE_SUBMITTING = 'SET_IS_PROMO_CODE_SUBMITTING',
    SET_USE_BALANCE = 'SET_USE_BALANCE',
    SET_BALANCE_AMOUNT = 'SET_BALANCE_AMOUNT',
}

export type CheckoutAction =
    | { type: CheckoutActionType.SET_ADDRESS_BOOK; payload: AddressWithId[] }
    | {
          type: CheckoutActionType.SET_AMENDED_TRANSACTION;
          payload: Updater<AmendedTransaction | undefined>;
      }
    | {
          type: CheckoutActionType.SET_PARENT_TRANSACTION;
          payload: TransactionSummary | undefined;
      }
    | {
          type: CheckoutActionType.SET_SHIPPING_OPTIONS;
          payload: ShippingOption[];
      }
    | {
          type: CheckoutActionType.SET_CREATED_TRANSACTION_INFO;
          payload: CreatedTransactionInfo;
      }
    | {
          type: CheckoutActionType.SET_SHIPPING_DETAILS;
          payload: ShippingFormValues | undefined;
      }
    | {
          type: CheckoutActionType.SET_CUSTOMER_DETAILS;
          payload: CustomerDetailsFormValues | undefined;
      }
    | {
          type: CheckoutActionType.SET_BILLING_ADDRESS;
          payload: Updater<TransactionAddressFormValues | undefined>;
      }
    | { type: CheckoutActionType.SET_IS_SUBMITTING; payload: boolean }
    | { type: CheckoutActionType.SET_IS_COMPLETE; payload: boolean }
    | {
          type: CheckoutActionType.SET_SUBMISSION_ERROR;
          payload: CheckoutSubmissionError | null;
      }
    | {
          type: CheckoutActionType.SET_FINAL_CHECKOUT_TYPE;
          payload: CheckoutType;
      }
    | {
          type: CheckoutActionType.SET_PAYMENT_GATEWAYS;
          payload: PaymentGateway[] | undefined;
      }
    | { type: CheckoutActionType.SET_CHECKOUT_ID; payload: string }
    | { type: CheckoutActionType.SET_IS_LOADING; payload: boolean }
    | {
          type: CheckoutActionType.SET_TRANSACTION;
          payload: MixedTransaction;
      }
    | {
          type: CheckoutActionType.SET_TRANSACTION_METADATA;
          payload: TransactionMetadata;
      }
    | { type: CheckoutActionType.SET_IS_PAYMENT_LINK; payload: boolean }
    | {
          type: CheckoutActionType.SET_IS_PROMO_CODE_SUBMITTING;
          payload: boolean;
      }
    | {
          type: CheckoutActionType.SET_USE_BALANCE;
          payload: boolean;
      }
    | { type: CheckoutActionType.SET_BALANCE_AMOUNT; payload: number };

export const logStateChange = (
    state: CheckoutState,
    action: CheckoutAction
): void => {
    const { publicRuntimeConfig } = getConfig();
    if (publicRuntimeConfig.debugCheckout) {
        console.log(
            'Updating checkout state with action: ',
            action.type,
            action.payload,
            state
        );
    }
};

export const checkoutReducer = (
    state: CheckoutState,
    action: CheckoutAction
): CheckoutState => {
    logStateChange(state, action);
    switch (action.type) {
        case CheckoutActionType.SET_ADDRESS_BOOK:
            return { ...state, addressBook: action.payload };
        case CheckoutActionType.SET_AMENDED_TRANSACTION:
            return {
                ...state,
                amendedTransaction:
                    typeof action.payload === 'function'
                        ? (
                              action.payload as (
                                  prev: AmendedTransaction | undefined
                              ) => AmendedTransaction | undefined
                          )(state.amendedTransaction)
                        : action.payload,
            };
        case CheckoutActionType.SET_PARENT_TRANSACTION:
            return { ...state, parentTransaction: action.payload };
        case CheckoutActionType.SET_SHIPPING_OPTIONS:
            return { ...state, shippingOptions: action.payload };
        case CheckoutActionType.SET_CREATED_TRANSACTION_INFO:
            return { ...state, createdTransactionInfo: action.payload };
        case CheckoutActionType.SET_SHIPPING_DETAILS:
            return { ...state, shippingDetails: action.payload };
        case CheckoutActionType.SET_CUSTOMER_DETAILS:
            return { ...state, customerDetails: action.payload };
        case CheckoutActionType.SET_BILLING_ADDRESS:
            return {
                ...state,
                billingAddress:
                    typeof action.payload === 'function'
                        ? (
                              action.payload as (
                                  prev: TransactionAddressFormValues | undefined
                              ) => TransactionAddressFormValues | undefined
                          )(state.billingAddress)
                        : action.payload,
            };

        case CheckoutActionType.SET_IS_SUBMITTING:
            return { ...state, isSubmitting: action.payload };
        case CheckoutActionType.SET_IS_COMPLETE:
            return { ...state, isComplete: action.payload };
        case CheckoutActionType.SET_SUBMISSION_ERROR:
            return { ...state, submissionError: action.payload };
        case CheckoutActionType.SET_FINAL_CHECKOUT_TYPE:
            return { ...state, finalCheckoutType: action.payload };
        case CheckoutActionType.SET_PAYMENT_GATEWAYS:
            return { ...state, paymentGateways: action.payload };
        case CheckoutActionType.SET_CHECKOUT_ID:
            return { ...state, checkoutId: action.payload };
        case CheckoutActionType.SET_IS_PAYMENT_LINK:
            return { ...state, isPaymentLink: action.payload };
        case CheckoutActionType.SET_IS_LOADING:
            return { ...state, isLoading: action.payload };
        case CheckoutActionType.SET_TRANSACTION:
            return { ...state, transaction: action.payload };
        case CheckoutActionType.SET_TRANSACTION_METADATA:
            return { ...state, transactionMetadata: action.payload };
        case CheckoutActionType.SET_IS_PROMO_CODE_SUBMITTING:
            return { ...state, isPromoCodeSubmitting: action.payload };
        case CheckoutActionType.SET_USE_BALANCE:
            return { ...state, useBalance: action.payload };
        case CheckoutActionType.SET_BALANCE_AMOUNT:
            return { ...state, balanceAmount: action.payload };
        default:
            return state;
    }
};
